import type { ReactElement } from 'react';
import { Modal } from 'antd';
import { Keyboard, X, Sparkles } from 'lucide-react';
import { useTranslation } from '@govard/i18n-core/react';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutGroup {
  title: string;
  items: {
    keys: string[];
    description: string;
  }[];
}

export function KeyboardShortcutsModal({
  isOpen,
  onClose,
}: KeyboardShortcutsModalProps): ReactElement {
  const { t } = useTranslation();

  const shortcutGroups: ShortcutGroup[] = [
    {
      title: t('crm.shortcuts.group_global'),
      items: [
        {
          keys: ['⌘', 'K'],
          description: t('crm.shortcuts.command_menu'),
        },
        {
          keys: ['⌘', 'J'],
          description: t('crm.shortcuts.ai_copilot'),
        },
        {
          keys: ['?'],
          description: t('crm.shortcuts.cheatsheet'),
        },
      ],
    },
    {
      title: t('crm.shortcuts.group_navigation'),
      items: [
        {
          keys: ['G', 'D'],
          description: t('crm.shortcuts.goto_deals'),
        },
        {
          keys: ['G', 'C'],
          description: t('crm.shortcuts.goto_contacts'),
        },
        {
          keys: ['G', 'T'],
          description: t('crm.shortcuts.goto_tasks'),
        },
        {
          keys: ['G', 'A'],
          description: t('crm.shortcuts.goto_analytics'),
        },
      ],
    },
    {
      title: t('crm.shortcuts.group_actions'),
      items: [
        {
          keys: ['C'],
          description: t('crm.shortcuts.create_deal'),
        },
        {
          keys: ['N'],
          description: t('crm.shortcuts.quick_note'),
        },
      ],
    },
  ];

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={480}
      styles={{
        body: {
          padding: 0,
          borderRadius: '16px',
          overflow: 'hidden',
          backgroundColor: 'var(--ant-color-bg-elevated)',
        },
      }}
    >
      <div className="flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-stone-200/80 px-5 py-4 bg-white">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 text-amber-700">
              <Keyboard size={16} strokeWidth={1.5} />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-stone-900 m-0">
                {t('crm.shortcuts.title')}
              </h2>
              <p className="text-[11px] text-stone-500 m-0">{t('crm.shortcuts.subtitle')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('crm.command_menu.close')}
            className="rounded-lg p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700 transition cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto p-5 space-y-5">
          {shortcutGroups.map((group) => (
            <div key={group.title}>
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-stone-400 mb-2.5">
                {group.title}
              </h3>
              <div className="space-y-1.5">
                {group.items.map((item) => (
                  <div
                    key={item.description}
                    className="flex items-center justify-between rounded-lg border border-stone-100 bg-white px-3 py-2 text-xs hover:border-amber-200 transition"
                  >
                    <span className="text-stone-700 font-medium">{item.description}</span>
                    <div className="flex items-center gap-1">
                      {item.keys.map((k) => (
                        <kbd
                          key={k}
                          className="min-w-5 rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-center font-mono text-[10px] font-semibold text-stone-600 shadow-2xs"
                        >
                          {k}
                        </kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-stone-200/80 bg-stone-50/70 px-5 py-2.5 text-[11px] text-stone-500">
          <div className="flex items-center gap-1.5">
            <Sparkles size={13} className="text-amber-600" />
            <span>GovardOS Pro Shortcuts</span>
          </div>
          <span className="text-[10px] text-stone-400">Esc to close</span>
        </div>
      </div>
    </Modal>
  );
}
